import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  CalibrationMeasurementSet,
  CalibrationStatus,
} from "../../API/calibration";
import { useAuth } from "../../API/auth";

export const TRACKED_CALIBRATIONS_STORAGE_KEY = "aureaTrackedCalibrations";
const MAX_AGE_MS = 24 * 60 * 60_000;
const STALLED_AFTER_MS = 60 * 60_000;

export type TrackedStatus = CalibrationStatus | "unknown" | "stalled";

export type TrackedCalibration = {
  id: string;
  version?: number;
  createdAt?: string;
  sourceResultSetId: string;
  resultSetId: string;
  customerId: string;
  fieldId: string;
  fieldName: string;
  status: TrackedStatus;
  errorCode?: string;
  trackedAt: string;
  username: string | null;
  hidden: boolean;
  dismissed: boolean;
};

type NewCalibration = Pick<
  TrackedCalibration,
  | "id"
  | "version"
  | "createdAt"
  | "sourceResultSetId"
  | "resultSetId"
  | "customerId"
  | "fieldId"
  | "fieldName"
>;

type TrackedCalibrationsState = {
  calibrations: TrackedCalibration[];
  track: (calibration: NewCalibration) => void;
  update: (measurementSet: CalibrationMeasurementSet) => void;
  setStatus: (
    sourceResultSetId: string,
    id: string,
    status: TrackedStatus,
  ) => void;
  hide: (sourceResultSetId: string) => void;
  dismiss: (sourceResultSetId: string) => void;
  forgetOtherUsers: (username: string) => void;
  pruneExpired: () => void;
};

export function isStalled(createdAt: string | undefined): boolean {
  return (
    createdAt !== undefined &&
    Date.now() - Date.parse(createdAt) >= STALLED_AFTER_MS
  );
}

function isNewer(candidate: NewCalibration, current: TrackedCalibration) {
  if (candidate.id === current.id) return false;
  if (candidate.version === undefined) return true;
  if (current.version !== undefined) {
    return candidate.version > current.version;
  }
  if (candidate.createdAt && current.createdAt) {
    return candidate.createdAt > current.createdAt;
  }
  return false;
}

function newEntry(calibration: NewCalibration): TrackedCalibration {
  return {
    ...calibration,
    status: "in_progress",
    trackedAt: new Date().toISOString(),
    username: useAuth.getState().username,
    hidden: false,
    dismissed: false,
  };
}

export const useTrackedCalibrations = create(
  persist<TrackedCalibrationsState>(
    (set, get) => {
      const updateEntry = (
        sourceResultSetId: string,
        id: string,
        changes: Partial<TrackedCalibration>,
      ) => {
        const isTarget = (tracked: TrackedCalibration) =>
          tracked.sourceResultSetId === sourceResultSetId && tracked.id === id;
        const current = get().calibrations.find(isTarget);
        const changed =
          current &&
          Object.entries(changes).some(
            ([key, value]) =>
              current[key as keyof TrackedCalibration] !== value,
          );
        if (!changed) return;

        set(({ calibrations }) => ({
          calibrations: calibrations.map((tracked) =>
            isTarget(tracked) ? { ...tracked, ...changes } : tracked,
          ),
        }));
      };

      return {
        calibrations: [],
        track: (calibration) =>
          set(({ calibrations }) => {
            const current = calibrations.find(
              (tracked) =>
                tracked.sourceResultSetId === calibration.sourceResultSetId,
            );
            if (current && !isNewer(calibration, current)) return {};
            return {
              calibrations: [
                ...calibrations.filter((tracked) => tracked !== current),
                newEntry(calibration),
              ],
            };
          }),
        update: (measurementSet) =>
          updateEntry(measurementSet.source_result_set_id, measurementSet.id, {
            status: measurementSet.status ?? "unknown",
            errorCode: measurementSet.error?.code,
            version: measurementSet.version,
            createdAt: measurementSet.created_at,
          }),
        setStatus: (sourceResultSetId, id, status) =>
          updateEntry(sourceResultSetId, id, { status }),
        hide: (sourceResultSetId) =>
          set(({ calibrations }) => ({
            calibrations: calibrations.map((tracked) =>
              tracked.sourceResultSetId === sourceResultSetId
                ? { ...tracked, hidden: true }
                : tracked,
            ),
          })),
        dismiss: (sourceResultSetId) =>
          set(({ calibrations }) => ({
            calibrations: calibrations.map((tracked) =>
              tracked.sourceResultSetId === sourceResultSetId
                ? { ...tracked, dismissed: true }
                : tracked,
            ),
          })),
        forgetOtherUsers: (username) => {
          if (get().calibrations.every((c) => c.username === username)) return;
          set(({ calibrations }) => ({
            calibrations: calibrations.filter(
              (tracked) => tracked.username === username,
            ),
          }));
        },
        pruneExpired: () =>
          set(({ calibrations }) => ({
            calibrations: calibrations.filter(
              (tracked) =>
                Date.now() - Date.parse(tracked.trackedAt) < MAX_AGE_MS,
            ),
          })),
      };
    },
    {
      name: TRACKED_CALIBRATIONS_STORAGE_KEY,
      version: 1,
      migrate: () =>
        ({ calibrations: [] }) as unknown as TrackedCalibrationsState,
    },
  ),
);
